import { User as EventResponseUser } from 'src/app/types/event-response.type';
import { AddressComponentsType, LocationType } from '../types/location.type';
import { Injectable, isDevMode } from '@angular/core';
import { AuthService, User as AuthUser } from './auth.service';
import intlTelInput from 'intl-tel-input';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { DateTimeService } from './date-time.service';
import { NavigationItem } from '../components/desktop-navigation-dropdown/types';
import {
  DEV_URL,
  ICONS,
  LOCALHOST_URL,
  REGISTRATION_TYPES,
  ROLE_ID_BY_REGISTRATION_TYPE,
} from '../constants/constants';
import { ROUTES } from '../constants/routes';

@Injectable()
export class UtilsService {
  private static usedUniqueIds: Array<string> = [];

  static getDate(): string {
    const date = new Date();
    date.setHours(12);
    date.setMinutes(0);
    date.setSeconds(0);
    return DateTimeService.getDateISOString(date, true);
  }

  static priceSum(values: number[]): number {
    return (
      values.reduce((acc, val) => {
        acc = acc + val * 100;
        return acc;
      }, 0) / 100
    );
  }

  static getAddressObject(
    address_components: google.maps.GeocoderAddressComponent[]
  ): AddressComponentsType {
    const locationComponentMapping: Record<
      keyof AddressComponentsType,
      string[]
    > = {
      street_number: ['street_number'],
      postal_code: ['postal_code'],
      street_address: ['street_address', 'route'],
      region: [
        'administrative_area_level_1',
        'administrative_area_level_2',
        'administrative_area_level_3',
        'administrative_area_level_4',
        'administrative_area_level_5',
      ],
      city: [
        'locality',
        'sublocality',
        'sublocality_level_1',
        'sublocality_level_2',
        'sublocality_level_3',
        'sublocality_level_4',
      ],
      country: ['country'],
      country_code: ['country'],
    };

    const location: AddressComponentsType = {
      street_number: '',
      postal_code: '',
      street_address: '',
      region: '',
      city: '',
      country: '',
      country_code: '',
    };

    address_components.forEach((component) => {
      Object.entries(locationComponentMapping).forEach(
        ([componentName, mappingOptions]) => {
          if (mappingOptions.includes(component.types[0])) {
            location[componentName as keyof AddressComponentsType] =
              componentName === 'country_code'
                ? component.short_name
                : component.long_name;
          }
        }
      );
    });

    return location;
  }

  static parseLocation(
    eventLocation: string | LocationType = ''
  ): LocationType | undefined {
    const locationFallback: LocationType = {
      name: '',
      formatted_address: '',
      place_id: '',
      is_point_on_map: false,
      lat: '',
      lng: '',
      ...UtilsService.getAddressObject([]),
    };

    if (typeof eventLocation === 'string') {
      try {
        const locationFromString = JSON.parse(eventLocation);

        const location = {
          ...locationFallback,
          ...locationFromString,
          ...UtilsService.getAddressObject(
            locationFromString.address_components
          ),
        };

        location.lat = String(locationFromString.location.lat);
        location.lng = String(locationFromString.location.lng);

        delete location.location;
        delete location.address_components;

        return location;
      } catch {
        return undefined;
      }
    }

    return eventLocation;
  }

  static getLocationDisplayName(
    location: string | LocationType | undefined
  ): string {
    if (typeof location === 'string') {
      location = UtilsService.parseLocation(location);
    }

    if (location === undefined) {
      return '';
    }

    if (location.formatted_address.includes(location.name)) {
      return location.formatted_address;
    }

    return [location.name, location.formatted_address].join(', ');
  }

  /**
   * @example
   * date = 2023-01-15T07:00:00"
   * machines timezone = -4
   * the JS date instance = Thu Feb 13 2021 3:00:00 GMT-0400 (Atlantic Standard Time)

   */
  static adjustDateForTimezone(date: string | number): Date {
    const tmpDate = new Date(date);

    tmpDate.setMinutes(tmpDate.getMinutes() + tmpDate.getTimezoneOffset());

    return tmpDate;
  }

  static getSetting(
    setting: string,
    user?: AuthUser | EventResponseUser
  ): string | undefined {
    return user?.settings?.[setting];
  }

  static initIntlTelInput(
    nativeInputElement: HTMLInputElement
  ): intlTelInput.Plugin {
    return intlTelInput(nativeInputElement, {
      formatOnDisplay: true,
      separateDialCode: true,
      initialCountry: 'auto',
      geoIpLookup: (resolve) => {
        fetch('https://ipapi.co/json')
          .then((res) => res.json())
          .then((data) => resolve(data.country_code))
          .catch(() => resolve('LV'));
      },
      utilsScript: 'assets/js/intl/utils.js',
    });
  }

  static getUniqueId(): string {
    let id = Math.random().toString(32);

    if (UtilsService.usedUniqueIds.includes(id)) {
      id = UtilsService.getUniqueId();
    }

    UtilsService.usedUniqueIds.push(id);

    return id;
  }

  static dateToNgbDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  static getNavigationItems(authService: AuthService): NavigationItem[] {
    const navigation: NavigationItem[] = [];

    if (authService.user) {
      navigation.push(
        {
          title: 'navigation_my_calender',
          icon: ICONS.CALENDAR_HEART_02_16,
          path: `/${ROUTES.MY_CALENDAR}`,
        },
        {
          title: 'navigation_my_account',
          icon: ICONS.USER_16,
          path: `/${ROUTES.MY_ACCOUNT}`,
          divider: true,
        }
      );

      if (
        authService.user?.role.id !==
        ROLE_ID_BY_REGISTRATION_TYPE[REGISTRATION_TYPES.ENTHUSIAST]
      ) {
        navigation.unshift(
          {
            title: 'navigation_my_category_management',
            icon: ICONS.EDIT_V2_16,
            path: `/${ROUTES.CMS_CATEGORY_MANAGEMENT}`,
          },
          {
            title: 'navigation_my_organization_management',
            icon: ICONS.EDIT_V2_16,
            path: `/${ROUTES.CMS_ORGANIZATION_MANAGEMENT}`,
          },
          {
            title: 'navigation_my_event_management',
            icon: ICONS.EDIT_V2_16,
            path: `/${ROUTES.CMS_EVENT_MANAGEMENT}`,
            divider: true,
          }
        );
      }
    }

    return navigation;
  }

  static isProd(): boolean {
    return !isDevMode() && !window.location.host.startsWith(DEV_URL);
  }

  static isLocalHost(): boolean {
    return window.location.host.startsWith(LOCALHOST_URL);
  }
}
